/**
 * Per-room message queue service implementing CLIENT A_25423.
 *
 * Requirements (gemSpec_TI-M_Basis_V1.1.2 §A_25423):
 *  - Exponential-backoff retry on send failure for up to 3 minutes.
 *  - After 3 minutes mark message as "unsent" so users can manually resend.
 *  - Per-room queue preserving send order (head-of-line blocking per room, not global).
 *  - Sequential send: wait for response before sending the next queued message.
 *
 * The service is a lightweight EventTarget so React hooks can subscribe to
 * state changes without importing React into the service layer.
 */
import logger from '../utils/logger';
import networkService from './networkService';

const isNetworkError = (error) => {
  if (!error) return false;
  if (error.name === 'NetworkError' || error.name === 'ConnectionError') return true;
  if (error.errcode === 'M_NETWORK_ERROR') return true;

  const message = typeof error.message === 'string' ? error.message.toLowerCase() : '';
  return message.includes('network') || message.includes('offline') || message.includes('fetch');
};

// ── Retry configuration ──────────────────────────────────────────────────────

/** Base delay for exponential backoff (ms). */
const BASE_RETRY_DELAY_MS = 1000;

/** Maximum single retry delay (ms). Cap prevents absurdly long waits. */
const MAX_RETRY_DELAY_MS = 30000;

/** Total wall-clock time allowed for retries before marking "unsent" (ms). */
export const MAX_RETRY_DURATION_MS = 3 * 60 * 1000; // 3 minutes

// ── Queue item states ────────────────────────────────────────────────────────

export const MessageStatus = Object.freeze({
  QUEUED: 'queued',
  SENDING: 'sending',
  RETRYING: 'retrying',
  SENT: 'sent',
  UNSENT: 'unsent',
});

export const MessageQueueErrorCode = Object.freeze({
  DISMISSED: 'MESSAGE_QUEUE_DISMISSED',
  DESTROYED: 'MESSAGE_QUEUE_DESTROYED',
});

const createQueueAbortError = (message, code) => {
  const error = new Error(message);
  error.name = 'AbortError';
  error.code = code;
  error.isQueueLifecycleError = true;
  return error;
};

// ── Non-retryable HTTP status codes ──────────────────────────────────────────

const NON_RETRYABLE_STATUSES = new Set([400, 403, 413, 431]);

const isRetryableError = (error) => {
  if (!error) return false;
  // Permission / payload errors are permanent – don't retry
  if (error.httpStatus && NON_RETRYABLE_STATUSES.has(error.httpStatus)) return false;
  if (error.errcode === 'M_FORBIDDEN' || error.errcode === 'M_TOO_LARGE') return false;
  // Everything else (network errors, 429, 5xx, timeouts) is retryable
  return true;
};

// ── Per-room queue ───────────────────────────────────────────────────────────

let nextItemId = 1;

const createTransactionId = (roomId, itemId) => `retry-${Date.now()}-${itemId}-${roomId}`;

class RoomQueue {
  constructor(roomId, onChange, getNetworkState) {
    this.roomId = roomId;
    /** @type {Array<QueueItem>} */
    this.items = [];
    this.processing = false;
    this.destroyed = false;
    this._onChange = onChange;
    this._getNetworkState = getNetworkState;
  }

  /** Enqueue a send operation. Returns a promise that resolves with the SDK result or rejects for permanent failure. */
  enqueue(sendFn, meta = {}) {
    return new Promise((resolve, reject) => {
      const itemId = nextItemId++;
      const item = {
        id: itemId,
        status: MessageStatus.QUEUED,
        sendFn,
        meta,
        resolve,
        reject,
        promiseSettled: false,
        retryCount: 0,
        firstAttemptTime: null,
        error: null,
        txnId: createTransactionId(this.roomId, itemId),
        awaitingNetwork: false,
        offlineSince: null,
        retryDueAt: null,
      };
      this.items.push(item);
      logger.info('[MessageQueue] Enqueued message', {
        roomId: this.roomId,
        itemId: item.id,
        txnId: item.txnId,
        type: meta.type,
      });
      this._notify();
      this._processNextSafely();
    });
  }

  /** Manually retry all unsent items (user-triggered resend). */
  retryUnsent() {
    let changed = false;
    for (const item of this.items) {
      if (item.status === MessageStatus.UNSENT) {
        item.status = MessageStatus.QUEUED;
        item.retryCount = 0;
        item.firstAttemptTime = null;
        item.error = null;
        item.awaitingNetwork = false;
        item.offlineSince = null;
        item.retryDueAt = null;
        changed = true;
      }
    }
    if (changed) {
      logger.info('[MessageQueue] Retrying unsent messages', {
        roomId: this.roomId,
      });
      this._notify();
      this._processNextSafely();
    }
  }

  /** Remove a specific unsent item (user dismisses it). */
  dismiss(itemId) {
    const idx = this.items.findIndex((i) => i.id === itemId);
    if (idx !== -1 && this.items[idx].status === MessageStatus.UNSENT) {
      const [removed] = this.items.splice(idx, 1);
      this._rejectItem(
        removed,
        createQueueAbortError('Message dismissed by user', MessageQueueErrorCode.DISMISSED),
      );
      this._disposeItem(removed);
      this._notify();
    }
  }

  /** True when the queue has items that are sending, retrying, or queued. */
  get isBusy() {
    return this.items.some(
      (i) => i.status === MessageStatus.QUEUED ||
             i.status === MessageStatus.SENDING ||
             i.status === MessageStatus.RETRYING,
    );
  }

  /** True when a retry attempt is actively in progress (retryCount > 0). */
  get isRetrying() {
    return this.items.some(
      (i) => i.retryCount > 0 &&
             (i.status === MessageStatus.SENDING ||
              i.status === MessageStatus.RETRYING ||
              i.status === MessageStatus.QUEUED),
    );
  }

  /** True when the queue has unsent items the user can retry. */
  get hasUnsent() {
    return this.items.some((i) => i.status === MessageStatus.UNSENT);
  }

  /** Snapshot of item statuses for the UI. */
  snapshot() {
    return this.items.map(({ id, status, meta, error, retryCount, txnId }) => ({
      id, status, meta, error, retryCount, txnId,
    }));
  }

  // ── internal ─────────────────────────────────────────────────────────────

  async _processNext() {
    if (this.processing || this.destroyed) return;

    // Find next item to send (first queued)
    const item = this.items.find(
      (i) => i.status === MessageStatus.QUEUED || i.status === MessageStatus.RETRYING,
    );
    if (!item) return;

    // If we're currently offline and this is a retry attempt, don't consume
    // the retry window by attempting another send; pause and wait for reconnect.
    // Allow the very first send attempt (retryCount === 0) even when offline so
    // callers can perform local-echo or observe immediate synchronous failures.
    if (!this._getNetworkState() && item.retryCount > 0) {
      this._waitForReconnect(item, 'pre-retry-offline');
      return;
    }

    this.processing = true;
    item.status = MessageStatus.SENDING;
    if (!item.firstAttemptTime) item.firstAttemptTime = Date.now();
    this._notify();

    try {
      const elapsedBeforeAttempt = Date.now() - item.firstAttemptTime;
      const remainingTimeMs = Math.max(0, MAX_RETRY_DURATION_MS - elapsedBeforeAttempt);

      logger.info('[MessageQueue] Starting send attempt', {
        roomId: this.roomId,
        itemId: item.id,
        txnId: item.txnId,
        retryCount: item.retryCount,
        elapsedBeforeAttempt,
        remainingTimeMs,
      });

      if (remainingTimeMs <= 0) {
        item.status = MessageStatus.UNSENT;
        this.processing = false;
        logger.warn('[MessageQueue] Retry window exhausted before send attempt', {
          roomId: this.roomId,
          itemId: item.id,
          txnId: item.txnId,
        });
        this._notify();
        this._processNextSafely();
        return;
      }

      const result = await item.sendFn({
        txnId: item.txnId,
        retryCount: item.retryCount,
        itemId: item.id,
        remainingTimeMs,
      });
      if (this.destroyed) {
        this.processing = false;
        this._disposeItem(item);
        return;
      }
      item.status = MessageStatus.SENT;
      this._notify();
      logger.info('[MessageQueue] Send succeeded', {
        roomId: this.roomId,
        itemId: item.id,
        txnId: item.txnId,
        retryCount: item.retryCount,
      });
      this._resolveItem(item, result);
      this._cleanup(item);
      // Chain to next queued item
      this.processing = false;
      this._processNextSafely();
    } catch (error) {
      if (this.destroyed) {
        this.processing = false;
        this._disposeItem(item);
        return;
      }
      logger.warn(`[MessageQueue] Send failed for room ${this.roomId} (attempt ${item.retryCount + 1}):`, error.message);
      item.retryCount++;
      item.error = error;

      const elapsed = Date.now() - item.firstAttemptTime;

      if (!isRetryableError(error) || elapsed >= MAX_RETRY_DURATION_MS) {
        // Permanent failure or exhausted retry window
        item.status = MessageStatus.UNSENT;
        logger.warn('[MessageQueue] Marking message unsent', {
          roomId: this.roomId,
          itemId: item.id,
          txnId: item.txnId,
          retryCount: item.retryCount,
          elapsed,
          reason: !isRetryableError(error) ? 'non-retryable-error' : 'retry-window-exhausted',
          errorName: error?.name,
          errorMessage: error?.message,
          errorTimeoutMs: error?.timeoutMs,
        });
        this._notify();
        this.processing = false;
        // Don't reject – let the item sit so users can manually resend.
        // Continue processing remaining queued items behind it.
        this._processNextSafely();
        return;
      }

      // Schedule retry with exponential backoff + jitter
      item.status = MessageStatus.RETRYING;
      this._notify();

      const delay = Math.min(
        BASE_RETRY_DELAY_MS * Math.pow(2, item.retryCount - 1),
        MAX_RETRY_DELAY_MS,
      );
      // Add ±20% jitter to prevent thundering herd
      const jitter = delay * 0.2 * (Math.random() * 2 - 1);
      const effectiveDelay = Math.max(0, Math.round(delay + jitter));
      const remainingTime = Math.max(0, MAX_RETRY_DURATION_MS - elapsed);
      const backoffExponent = Math.max(0, item.retryCount - 1);

      logger.debug('[MessageQueue] Backoff calculation', {
        roomId: this.roomId,
        itemId: item.id,
        txnId: item.txnId,
        retryCount: item.retryCount,
        baseRetryDelayMs: BASE_RETRY_DELAY_MS,
        backoffExponent,
        uncappedDelayMs: BASE_RETRY_DELAY_MS * Math.pow(2, backoffExponent),
        cappedDelayMs: delay,
        jitterMs: Math.round(jitter),
        effectiveDelayMs: effectiveDelay,
        elapsedMs: elapsed,
        remainingRetryWindowMs: remainingTime,
      });

      logger.info('[MessageQueue] Scheduling retry', {
        roomId: this.roomId,
        itemId: item.id,
        txnId: item.txnId,
        retryCount: item.retryCount,
        delay,
        effectiveDelay,
        remainingTime,
      });

      if (!this._getNetworkState() && isNetworkError(error)) {
        this._waitForReconnect(item, 'send-failed-offline');
        return;
      }

      // Do not schedule another send attempt beyond the retry window.
      // Instead, wait only until the cutoff and then mark the message unsent.
      if (effectiveDelay >= remainingTime) {
        item.retryDueAt = Date.now() + remainingTime;
        logger.debug('[MessageQueue] Retry delay exceeds remaining retry window', {
          roomId: this.roomId,
          itemId: item.id,
          txnId: item.txnId,
          retryCount: item.retryCount,
          effectiveDelayMs: effectiveDelay,
          remainingRetryWindowMs: remainingTime,
          cutoffAtMs: item.retryDueAt,
        });
        item._retryTimer = setTimeout(() => {
          item._retryTimer = null;
          item.retryDueAt = null;
          item.status = MessageStatus.UNSENT;
          this.processing = false;
          logger.warn('[MessageQueue] Retry delay would exceed retry window; marking unsent at cutoff', {
            roomId: this.roomId,
            itemId: item.id,
            txnId: item.txnId,
            retryCount: item.retryCount,
            remainingTime,
          });
          this._notify();
          this._processNextSafely();
        }, remainingTime);
        return;
      }

      item.retryDueAt = Date.now() + effectiveDelay;
      logger.debug('[MessageQueue] Retry timer armed', {
        roomId: this.roomId,
        itemId: item.id,
        txnId: item.txnId,
        retryCount: item.retryCount,
        retryAtMs: item.retryDueAt,
        effectiveDelayMs: effectiveDelay,
      });
      item._retryTimer = setTimeout(() => {
        item._retryTimer = null;
        item.retryDueAt = null;
        item.status = MessageStatus.QUEUED;
        this.processing = false;
        logger.debug('[MessageQueue] Retry timer fired; queueing next send attempt', {
          roomId: this.roomId,
          itemId: item.id,
          txnId: item.txnId,
          retryCount: item.retryCount,
        });
        this._processNextSafely();
      }, effectiveDelay);
    }
  }

  _processNextSafely() {
    this._processNext().catch((error) => {
      logger.error('[MessageQueue] Unhandled queue processing error', {
        roomId: this.roomId,
        errorName: error?.name,
        errorMessage: error?.message,
      });
      this.processing = false;
    });
  }

  _cleanup(item) {
    const idx = this.items.indexOf(item);
    if (idx !== -1) {
      this.items.splice(idx, 1);
    }
    this._disposeItem(item);
  }

  _resolveItem(item, result) {
    if (item.promiseSettled) return;
    item.promiseSettled = true;
    const resolve = item.resolve;
    item.resolve = null;
    item.reject = null;
    resolve?.(result);
  }

  _rejectItem(item, error) {
    if (item.promiseSettled) return;
    item.promiseSettled = true;
    const reject = item.reject;
    item.resolve = null;
    item.reject = null;
    reject?.(error);
  }

  _disposeItem(item) {
    if (item._retryTimer) {
      clearTimeout(item._retryTimer);
      item._retryTimer = null;
    }

    item.retryDueAt = null;
    item.awaitingNetwork = false;
    item.offlineSince = null;
    item.firstAttemptTime = null;
    item.error = null;
    item.sendFn = null;
    item.meta = null;
  }

  _notify() {
    this._onChange(this.roomId);
  }

  _waitForReconnect(item, reason) {
    if (item._retryTimer) {
      clearTimeout(item._retryTimer);
      item._retryTimer = null;
    }

    item.retryDueAt = null;
    if (!item.awaitingNetwork) {
      item.awaitingNetwork = true;
      item.offlineSince = Date.now();
    }

    logger.debug('[MessageQueue] Pausing retry until network returns', {
      roomId: this.roomId,
      itemId: item.id,
      txnId: item.txnId,
      retryCount: item.retryCount,
      reason,
      offlineSinceMs: item.offlineSince,
      firstAttemptTimeMs: item.firstAttemptTime,
    });

    logger.info('[MessageQueue] Waiting for network reconnect', {
      roomId: this.roomId,
      itemId: item.id,
      txnId: item.txnId,
      retryCount: item.retryCount,
      reason,
    });
  }

  handleNetworkOffline() {
    const activeRetry = this.items.find((item) => item.status === MessageStatus.RETRYING);
    if (!activeRetry) return;

    this._waitForReconnect(activeRetry, 'browser-offline');
    this._notify();
  }

  handleNetworkOnline() {
    const activeRetry = this.items.find((item) => item.awaitingNetwork);
    if (!activeRetry) return;

    const offlineDuration = activeRetry.offlineSince ? Date.now() - activeRetry.offlineSince : 0;
    const adjustedFirstAttemptTime = activeRetry.firstAttemptTime && offlineDuration > 0
      ? activeRetry.firstAttemptTime + offlineDuration
      : activeRetry.firstAttemptTime;

    logger.debug('[MessageQueue] Resuming retry after reconnect', {
      roomId: this.roomId,
      itemId: activeRetry.id,
      txnId: activeRetry.txnId,
      retryCount: activeRetry.retryCount,
      offlineDurationMs: offlineDuration,
      previousFirstAttemptTimeMs: activeRetry.firstAttemptTime,
      adjustedFirstAttemptTimeMs: adjustedFirstAttemptTime,
    });

    if (activeRetry.firstAttemptTime && offlineDuration > 0) {
      activeRetry.firstAttemptTime += offlineDuration;
    }

    activeRetry.awaitingNetwork = false;
    activeRetry.offlineSince = null;
    activeRetry.retryDueAt = null;

    logger.info('[MessageQueue] Network restored, retrying immediately', {
      roomId: this.roomId,
      itemId: activeRetry.id,
      txnId: activeRetry.txnId,
      retryCount: activeRetry.retryCount,
      wasProcessing: this.processing,
    });

    this.processing = false;
    this._notify();
    this._processNextSafely();
  }

  /** Cancel all pending timers (cleanup on room leave). */
  destroy() {
    if (this.destroyed) return;

    this.destroyed = true;
    this.processing = false;

    for (const item of this.items) {
      this._rejectItem(
        item,
        createQueueAbortError('Message queue destroyed', MessageQueueErrorCode.DESTROYED),
      );
      this._disposeItem(item);
    }

    this.items = [];
  }
}

// ── Singleton service (one queue map shared across the app) ──────────────────

class MessageQueueService extends EventTarget {
  constructor() {
    super();
    /** @type {Map<string, RoomQueue>} */
    this._queues = new Map();
    this._isOnline = networkService.getIsOnline();
    this._unsubscribeNetwork = networkService.subscribe(this._handleNetworkChange);
  }

  /**
   * Enqueue a send operation for a room.
   * @param {string} roomId
   * @param {Function} sendFn - Async function that performs the actual send (returns SDK result).
   * @param {Object} [meta] - Metadata for the UI (e.g. { type: 'text', body: '...' }).
   * @returns {Promise} Resolves with SDK result on success, or sits in UNSENT state.
   */
  enqueue(roomId, sendFn, meta = {}) {
    const queue = this._getOrCreateQueue(roomId);
    return queue.enqueue(sendFn, meta);
  }

  /** Retry all unsent messages in a room. */
  retryUnsent(roomId) {
    const queue = this._queues.get(roomId);
    if (queue) queue.retryUnsent();
  }

  /** Dismiss a specific unsent message. */
  dismiss(roomId, itemId) {
    const queue = this._queues.get(roomId);
    if (queue) queue.dismiss(itemId);
  }

  /** Whether a room's queue is actively processing (sending/retrying). */
  isBusy(roomId) {
    const queue = this._queues.get(roomId);
    return queue ? queue.isBusy : false;
  }

  /** Whether a room's queue is actively retrying a previously-failed message. */
  isRetrying(roomId) {
    const queue = this._queues.get(roomId);
    return queue ? queue.isRetrying : false;
  }

  /** Whether a room has unsent messages the user can retry. */
  hasUnsent(roomId) {
    const queue = this._queues.get(roomId);
    return queue ? queue.hasUnsent : false;
  }

  /** Get snapshot of a room's queue for rendering. */
  getQueueSnapshot(roomId) {
    const queue = this._queues.get(roomId);
    return queue ? queue.snapshot() : [];
  }

  /** Cleanup a room's queue (e.g. on room leave or logout). */
  destroyQueue(roomId) {
    const queue = this._queues.get(roomId);
    if (queue) {
      queue.destroy();
      this._queues.delete(roomId);
      this._emitChange(roomId);
    }
  }

  /** Cleanup all queues (logout). */
  destroyAll() {
    const roomIds = Array.from(this._queues.keys());

    for (const [, queue] of this._queues) {
      queue.destroy();
    }
    this._queues.clear();

    for (const roomId of roomIds) {
      this._emitChange(roomId);
    }

    // Reset the network subscription so this service starts fresh for the
    // next session (also prevents the old subscriber from leaking in tests).
    this._unsubscribeNetwork();
    this._isOnline = networkService.getIsOnline();
    this._unsubscribeNetwork = networkService.subscribe(this._handleNetworkChange);
  }

  // ── internal ─────────────────────────────────────────────────────────────

  _getOrCreateQueue(roomId) {
    if (!this._queues.has(roomId)) {
      this._queues.set(
        roomId,
        new RoomQueue(roomId, (rid) => this._emitChange(rid), () => networkService.getIsOnline()),
      );
    }
    return this._queues.get(roomId);
  }

  _emitChange(roomId) {
    this.dispatchEvent(new CustomEvent('queueChange', { detail: { roomId } }));
  }

  _handleNetworkChange = ({ isOnline }) => {
    this._isOnline = isOnline;
    for (const [, queue] of this._queues) {
      if (isOnline) {
        queue.handleNetworkOnline();
      } else {
        queue.handleNetworkOffline();
      }
    }
  };
}

const messageQueueService = new MessageQueueService();
export default messageQueueService;
